import type { Book } from '@wellread/api';
import { Link, useParams } from '@tanstack/react-router';
import { count } from '@wellread/core';
import { useBooks } from '../lib/queries';
import { type Missed, useUpload } from '../state/upload';
import { Button, Meter } from '../ui';

/**
 * Books on their way up, said where the reader is looking: at the head of
 * the page they added them from, whichever that is. Above what the page
 * scrolls, so it is in sight wherever they were. One book's goes when its
 * own page takes over; several stay where the reader is, and once they are
 * up the block lists those waiting for their track.
 */
export function UploadStatus() {
  const name = useUpload((s) => s.name);
  const progress = useUpload((s) => s.progress);
  const total = useUpload((s) => s.total);
  const sent = useUpload((s) => s.sent);
  const after = useUpload((s) => s.added.length + s.missed.length > 0);
  if (name) {
    return (
      <UploadBlock
        name={name}
        progress={progress}
        of={total > 1 ? { at: Math.min(sent + 1, total), total } : undefined}
      />
    );
  }
  return after ? <AddedStatus /> : null;
}

/** The block as drawn: the gallery renders this. progress: 0 to 1; of: which file of several this is. */
export function UploadBlock({
  name,
  progress,
  of,
}: {
  name: string;
  progress: number;
  of?: { at: number; total: number };
}) {
  const percent = Math.round(Math.max(0, Math.min(1, progress)) * 100);
  return (
    <section className="upl" aria-label={of ? 'Uploading books' : 'Uploading a book'}>
      <div className="card upl-card">
        <div className="upl-line">
          <span className="mini">{of ? `Uploading ${count(of.at)} of ${count(of.total)}` : 'Uploading'}</span>
          {/* The bar says the figure to a screen reader. */}
          <span className="upl-pct" aria-hidden="true">{`${percent}%`}</span>
        </div>
        <b className="upl-name">{name}</b>
        <Meter value={percent} label={`Uploading ${name}`} />
      </div>
    </section>
  );
}

/** Where a book is still to be told which track it is for: its file being read, or read and waiting. */
const waits = (b: Pick<Book, 'status'>) =>
  b.status === 'uploading' || b.status === 'parsing' || b.status === 'awaiting_confirm';

/**
 * Several books once they are up: those still waiting for their track, as
 * the shelf has them now. A book drops out of the list once the reader has
 * chosen (or kept it to read, or removed it), and the one whose page is open
 * isn't listed. With nothing left to say, it goes by itself.
 */
function AddedStatus() {
  const added = useUpload((s) => s.added);
  const missed = useUpload((s) => s.missed);
  const dismiss = useUpload((s) => s.dismiss);
  const books = useBooks();
  const { bookId } = useParams({ strict: false });
  const shelf = new Map((books.data?.books ?? []).map((b) => [b.id, b]));
  const waiting = added.flatMap((a) => {
    if (a.id === bookId) return [];
    const b = shelf.get(a.id);
    // Not on the shelf yet: it is on its way there, unless the shelf has been asked since and it has gone.
    if (!b) return books.data && !books.isFetching ? [] : [{ id: a.id, title: a.name, onto: a.onto }];
    return waits(b) ? [{ id: b.id, title: b.title, onto: a.onto }] : [];
  });
  // Over a book's own page, where its question is, only the next one is named.
  if (bookId) return waiting.length ? <AddedBlock books={waiting} missed={[]} next onDone={dismiss} /> : null;
  if (!waiting.length && !missed.length) return null;
  return <AddedBlock books={waiting} missed={missed} onDone={dismiss} />;
}

/** How many of the books waiting are named: the rest are counted, and wait in the library. */
const NAMED = 3;

/**
 * The block once several books are up, as drawn: the gallery renders this.
 * The books waiting for their track, a few by name with the way to choose
 * each one's (on the track they were added from, if they were), the rest
 * counted; then the files that didn't go, and why. Done puts it away. next:
 * only the first, under how many wait, so a book's own page keeps its
 * question in sight.
 */
export function AddedBlock({
  books,
  missed,
  next = false,
  onDone,
}: {
  books: readonly { id: string; title: string; onto?: string }[];
  missed: readonly Missed[];
  next?: boolean;
  onDone: () => void;
}) {
  const named = next ? 1 : NAMED;
  const rest = books.length - named;
  return (
    <section className="upl" aria-label="Books added">
      <div className="card upl-card">
        <div className="upl-line">
          <span className="mini">
            {next
              ? `${count(books.length)} more waiting for ${books.length === 1 ? 'its' : 'their'} track`
              : books.length
                ? 'Waiting for their track'
                : 'Not added'}
          </span>
          <Button variant="quiet" size="sm" onClick={onDone}>
            Done
          </Button>
        </div>
        {books.length ? (
          <ul className="upl-list">
            {books.slice(0, named).map((b) => (
              <li key={b.id} className="upl-row">
                <b className="upl-name">{b.title}</b>
                <Link
                  to="/books/$bookId"
                  params={{ bookId: b.id }}
                  search={b.onto ? { onto: b.onto } : {}}
                  className="btn btn-secondary btn-sm"
                  aria-label={`Choose the track for ${b.title}`}
                >
                  Choose its track
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
        {rest > 0 && !next ? (
          <p className="note">
            {`${rest === 1 ? 'One more waits' : `${count(rest)} more wait`} in `}
            <Link to="/library">your library</Link>.
          </p>
        ) : null}
        {missed.length ? (
          <>
            {books.length ? <span className="mini">Not added</span> : null}
            <ul className="upl-list">
              {missed.map((m, i) => (
                <li key={`${i}-${m.name}`} className="upl-miss">
                  <b className="upl-name">{m.name}</b>
                  <span className="small muted">{m.error}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </section>
  );
}
